import GridPagerZone, {gridPagerZoneProps} from "./GridPagerZone";
import React, {
    Dispatch,
    MouseEventHandler,
    ReactElement,
    ReactNode, SetStateAction,
    useCallback,
    useContext,
    useEffect, useMemo,
    useRef,
    useState
} from "react";
import FlintLoadingWrapper from "../common/loading/FlintLoadingWrapper";
import {useTranslation} from "react-i18next";
import axios, {AxiosRequestConfig, CancelTokenSource} from "axios";
import {ApiResponseDto, GridResult, IGridReadRequest, SelectionCellHeaderProps} from "@utils/Models";
import {
    Grid, GridCellProps, GridCellsSettings,
    GridColumn as Column,
    GridCustomCellProps, GridDetailRowProps,
    GridRowClickEvent, GridSelectableSettings, GridSelectionCell, GridSelectionChangeEvent,
    GridSortChangeEvent
} from "@progress/kendo-react-grid";
import GridActionColumnCell, {actionColumn} from "./GridActionColumnCell";
import {FlintNotificationContext, useFlintNotificationContext} from "../common/notifications/FlintNotificationContext";
import {mediaQueries, ThemeColoursType, VivoPortal} from "@utils/AppConstants";
import {SortDescriptor} from "@progress/kendo-data-query";
import GridPagingDefaultValue = VivoPortal.GridPagingDefaultValue;

import PageTitleHeaderComponent from "../common/page_ttle/PageTitleHeaderComponent";
import {FloatingActionButton} from "@progress/kendo-react-buttons";
import useVivoGridColumns from "./useVivoGridColumns";
import {SelectDescriptor} from "@progress/kendo-react-data-tools";
import * as icons from "@fortawesome/free-solid-svg-icons"
import useMediaQuery from "@utils/other/useMediaQuery";
export default function FlintGridWrapper<T>(props: flintGridWrapperProps<T>) {
    const [showLoading, setShowLoading] = useState(false);
    const {showNotification} = useFlintNotificationContext();
    const [data, setData] = useState<T[]>([]);
    const [sort, setSort] = useState<SortDescriptor[]>([]);
    const [pageSize, setPageSize] = useState<number>(GridPagingDefaultValue);
    const [skip, setSkip] = useState<number>(0);
    const [totalItems, setTotalItems] = useState<number>(0);
    const cancelTokenSourceRef = useRef<CancelTokenSource | null>(null);
    const {GridSelectionCell, GridSelectionHeaderCell} = useVivoGridColumns();

    const {t} = useTranslation();
    const {state, utils, handlers} = useFlintGridWrapper(props);
    useEffect(() => {
        loadGridData().catch(e => {
            showNotification(e);
        });
    }, [pageSize, skip, sort, props.gridRefreshTrigger, props.apiUrl]);
    const lockLoadingPanelRef = useRef(false);
    function handleBeforeDataBound(data:T[]){
        return props.onBeforeDataBound?.(data) ?? data;
    }
    async function loadGridData() {
        // Cancel the previous request if it exists
        if (cancelTokenSourceRef.current) {
            cancelTokenSourceRef.current.cancel("Operation canceled due to new request.");
            lockLoadingPanelRef.current = true;
        }
        // Create a new cancellation token
        cancelTokenSourceRef.current = axios.CancelToken.source();

        if (!props.apiUrl) {
            setData([]);
            // Clear the token after the request completes
            cancelTokenSourceRef.current = null;
            return;
        }

        setShowLoading(true);
        let request: IGridReadRequest = {
            ...props.requestParameters,
            GridPaging: {take: pageSize, skip: skip},
            GridSorting: sort ?? []
        } as IGridReadRequest;
        let config: AxiosRequestConfig = {
            cancelToken: cancelTokenSourceRef.current.token
        }
        try {
            let response = await axios.post<ApiResponseDto<GridResult<T>>>(props.apiUrl, request, config);
            let items = response.data.Result.Data;
            items = handleBeforeDataBound(items);
            setData(items);
            if (props.onDataLoaded)
                props.onDataLoaded(items);
            
            setTotalItems(response.data.Result.Total);
        } catch (err: any) {
            if (axios.isCancel(err)) {
                console.debug("Request canceled:", err.message);
            } else {
                showNotification(err);
            }
        } finally {
            if (!lockLoadingPanelRef.current)
                setShowLoading(false);
            else
                lockLoadingPanelRef.current = false;
            // Clear the token after the request completes
            cancelTokenSourceRef.current = null;
        }
    }

    const gridPagerProps: gridPagerZoneProps = {
        defaultPageSize: pageSize,
        totalItems: totalItems,
        onPageChange: (page: number) => {
            setSkip((page - 1) * pageSize);
        },
        onPageSizeSelected: (ps) => {
            setPageSize(ps);
        }
    }


    return (
        <>
            {props.pageTitle &&
                <PageTitleHeaderComponent pageTitle={t(props.pageTitle)}
                    //                          addNewButton={addNewButton}
                />}
            {props.headerNode}
            <FlintLoadingWrapper showLoading={showLoading ?? false}>
                <>
                    <div className={`grid-wrapper${props.onRowClicked ? " row-pointer" : ""}`}>
                        <Grid showLoader={props.isLoading}
                            adaptive={true}
                            autoProcessData={{group: true}}
                            groupable={props.groupable && !utils.forceHideGrouping}
                            data={data}
                            dataItemKey={"Id"}
                            style={{minHeight: "300px"}}
                            scrollable={"none"}
                            sort={sort}
                            sortable={true}
                            select={state.select.get}
                            onSelectionChange={handlers.handleSelectionChange}
                            selectable={state.selectable.get}
                            cells={state.cells.get}
                            onRowClick={handlers.handleRowClicked}
                            onSortChange={(e: GridSortChangeEvent) => {
                                setSort(e.sort);
                            }}>
                            {props.rowSelecting?.enabled && <Column
                                // headerClassName={"empty-grid-header"} 
                                width={50}
                                groupable={false}
                                cells={{
                                    select: {},
                                    headerCell: () =>
                                        (<GridSelectionHeaderCell
                                            {...props.rowSelecting?.selectionColumnHeaderProps ?? {iconVisible:false}}/>)
                                }}
                                columnType="checkbox"/>}
                            {props.children}
                            {state.actionColumns.get}
                        </Grid>
                    </div>
                    <GridPagerZone pagingSteps={gridPagerProps?.pagingSteps}
                                   totalItems={gridPagerProps?.totalItems ?? 0}
                                   defaultPageSize={gridPagerProps?.defaultPageSize}
                                   onPageSizeSelected={gridPagerProps?.onPageSizeSelected}
                                   onPageChange={gridPagerProps?.onPageChange}/>
                </>
            </FlintLoadingWrapper>
            {props.addNewButton &&
                <FloatingActionButton text={props.addNewButton?.text} size={"medium"} themeColor={"primary"}
                                      align={{horizontal: 'end', vertical: 'bottom'}} positionMode={"fixed"}
                                      alignOffset={{x:10,y:35}}
                                      onClick={props.addNewButton?.onClick}
                />}
        </>
    );
}

export interface flintGridWrapperProps<T> {
    isLoading? : boolean;
    pageTitle: string | null;
    groupable?: boolean;
    children: ReactNode; // grid columns
    apiUrl: string;
    requestParameters?: any;
    headerNode?: ReactElement;
    actionColumns?: actionColumn[];
    // fires whenever any new data is being loaded
    onDataLoaded?: (items: T[]) => void;
    gridRefreshTrigger?: number;
    onRowClicked?: (item: T) => void;
    /** optional add new item BUTTON */
    addNewButton?: { text: string, onClick: () => Promise<void> | void, themeColour?: ThemeColoursType }
    rowSelecting?: {
        enabled: boolean;
        SelectDescriptor: { get: SelectDescriptor, set: Dispatch<SetStateAction<SelectDescriptor>> }
        dataItemKey: string,
        selectionColumnHeaderProps? : SelectionCellHeaderProps
    },
    /** last moment to transform data before it will be bound to the grid datasource */
    onBeforeDataBound?:(data:T[])=>T[];
}

function useFlintGridWrapper<T>(props: flintGridWrapperProps<T>) {
    const {t} = useTranslation();
    const {GridSelectionCell, GridSelectionHeaderCell} = useVivoGridColumns();
    const isTouchScreen = useMediaQuery(mediaQueries.isTouch);
    const isXlUp = useMediaQuery(mediaQueries.xl_Up);
    const selectableParam: boolean | GridSelectableSettings | undefined = useMemo(() => {
        if (!props.rowSelecting?.enabled)
            return undefined;
        return {
            enabled: true,
            cell: false,
            drag: false,
            mode: 'multiple'
        }
    }, [props.rowSelecting?.enabled]);
    const cells: boolean | GridCellsSettings | undefined = useMemo(() => {
        if (!props.rowSelecting?.enabled)
            return undefined;
        return {
            select: {
                data: GridSelectionCell,
                groupHeader: undefined,
            }
        }
    }, [props.rowSelecting?.enabled]);
    const getActionColumns = useMemo(() => {
        if (props.rowSelecting?.enabled || (props.actionColumns?.length ?? 0) === 0)
            return null;
        return <Column headerClassName={"text-center"} className={"text-center"} sortable={false}
                       title={t("COMMON_Actions")} width={100} cells={{
            data: (p: GridCustomCellProps) => GridActionColumnCell({
                ...p,
                columns: props.actionColumns!
            })
        }}/>;
    }, [props.actionColumns, props.rowSelecting?.enabled]);
    const handleRowClicked = useMemo(() => {
        if (props.rowSelecting?.enabled || !props.onRowClicked)
            return undefined;
        return (e: GridRowClickEvent) => props.onRowClicked!(e.dataItem);
    }, [props.onRowClicked, props.rowSelecting?.enabled]);
    const handleSelectionChange = useCallback((event: GridSelectionChangeEvent) => {
        props.rowSelecting?.SelectDescriptor.set(event.select);
    }, []);
    return {
        state: {
            selectable: {get: selectableParam},
            cells: {get: cells},
            actionColumns: {get: getActionColumns},
            select: {get: props.rowSelecting?.SelectDescriptor.get}
        },
        handlers: {
            handleRowClicked,
            handleSelectionChange
        },
        utils: {
            forceHideGrouping : isTouchScreen || !isXlUp,
        },
    }
}